/**
 * WhatsApp Flow Preview Builder Component
 *
 * This component renders a visual preview of WhatsApp Flow JSON,
 * matching the Meta Flow Builder UI design exactly.
 *
 * Design Features:
 * - Landing screen with "Preview Flow" button
 * - Bottom-to-top slide animation for flow preview
 * - Mobile frame with gray rounded container
 * - WhatsApp-style header with close button and progress stepper
 * - Screen selector dropdown for quick navigation
 * - Dynamic navigation based on Flow JSON actions
 * - Visual progress indicator showing screen completion
 * - All inputs are interactive and functional
 * - Clean white background with proper spacing
 * - All WhatsApp Flow components support
 * - Professional UI matching Meta's exact design patterns
 *
 * @component
 * @param {Object} props
 * @param {Object} props.flow_json - The WhatsApp Flow JSON object to render
 * @param {any} props.selectedTemplate - Selected template identifier to trigger flow reload
 */

import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  CloseOutlined,
  CheckOutlined,
  DownOutlined,
  LeftOutlined,
} from "@ant-design/icons";
import { Checkbox, Radio, DatePicker, message, Dropdown, Menu } from "antd";
import dayjs from "dayjs";

const FLOW_TRANSITION_DURATION = 220;
const INPUT_BASE_CLASSES =
  "w-full px-3 py-3 text-sm !border !border-gray-300 !rounded-lg !focus:outline-none !focus:border-[#1daa61] !focus:ring-2 !focus:ring-[#1daa61] !focus:ring-opacity-40";
const TEXTAREA_BASE_CLASSES = `${INPUT_BASE_CLASSES} resize-none`;
const SELECT_BASE_CLASSES = `${INPUT_BASE_CLASSES} bg-white`;
const DATE_FORMAT = "YYYY-MM-DD";

const parseDateLimit = (value) => {
  if (!value) return null;
  const parsed = dayjs(value, DATE_FORMAT, true);
  return parsed.isValid() ? parsed : null;
};

const WhatsAppFlowPreview = ({
  flow_json,
  selectedTemplate,
  instantUpdate = false,
  externalCurrentScreenId,
  onScreenChange,
}) => {
  // State for showing/hiding the flow preview - default open
  const [isFlowOpen, setIsFlowOpen] = useState(true);
  console.log("flow_json", flow_json.screens);
  // State management for current screen and navigation
  const [currentScreenId, setCurrentScreenId] = useState(null);
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [formData, setFormData] = useState({});
  const [screenData, setScreenData] = useState({});
  const [activeFlow, setActiveFlow] = useState(flow_json);
  const [isFlowTransitioning, setIsFlowTransitioning] = useState(false);
  const isInitialRender = useRef(true);
  const [hasCompletedFlow, setHasCompletedFlow] = useState(false);
  
  const flowScreens = useMemo(() => flow_json?.screens ?? [], [flow_json]);
  const isReadMoreScreen = useCallback((screens, screen) => {
    const targetId = typeof screen === 'string' ? screen : screen?.id;
    if (!targetId || !Array.isArray(screens)) return false;

    const traverse = (node) => {
      if (!node) return false;

      // Arrays: check any child
      if (Array.isArray(node)) {
        return node.some(traverse);
      }

      // Objects: check this node, then descend
      if (typeof node === 'object') {
        // ✅ Check for OptIn -> on-click-action.next.name === targetId
        if (
          node.type === 'OptIn' &&
          node['on-click-action']?.next?.name === targetId
        ) {
          return true;
        }

        // Common WhatsApp Flow nesting
        if (node.layout && traverse(node.layout)) return true;
        if (node.children && traverse(node.children)) return true;

        // Be robust: scan any other nested objects/arrays too
        for (const key in node) {
          if (key === 'layout' || key === 'children') continue;
          const val = node[key];
          if (val && (typeof val === 'object' || Array.isArray(val))) {
            if (traverse(val)) return true;
          }
        }
      }

      return false;
    };

    // Look across ALL screens to find an OptIn linking to this targetId
    for (const s of screens) {
      if (traverse(s?.layout)) return true;
    }

    return false;
  }, [flowScreens]);

  const findFirstMainScreenId = useCallback((list) => {
    if (!Array.isArray(list)) return null;
    const mainScreen = list.find((screen) => !isReadMoreScreen(flowScreens, screen));
    return mainScreen?.id ?? list[0]?.id ?? null;
  }, [flowScreens]);
  const firstScreenId = useMemo(
    () => findFirstMainScreenId(flowScreens),
    [findFirstMainScreenId, flowScreens]
  );

  const findParentScreenIdForNext = useCallback((screens, nextScreenId) => {
    if (!nextScreenId || !Array.isArray(screens)) return null;

    // Recursively check if any node navigates to target
    const pointsToTarget = (node, targetId) => {
      if (!node) return false;

      // Arrays: any child matches
      if (Array.isArray(node)) return node.some((child) => pointsToTarget(child, targetId));

      // Objects
      if (typeof node === 'object') {
        // Check on-click-action -> next -> name === targetId
        const nextName = node?.['on-click-action']?.next?.name;
        if (nextName === targetId) return true;

        // Usual nesting
        if (node.layout && pointsToTarget(node.layout, targetId)) return true;
        if (node.children && pointsToTarget(node.children, targetId)) return true;

        // Scan other nested objects/arrays to be robust
        for (const key in node) {
          if (key === 'layout' || key === 'children') continue;
          const val = node[key];
          if (val && (typeof val === 'object' || Array.isArray(val))) {
            if (pointsToTarget(val, targetId)) return true;
          }
        }
      }

      return false;
    };

    // Walk each screen; the first one that points to target is the "parent"
    for (const scr of screens) {
      if (pointsToTarget(scr?.layout, nextScreenId)) {
        return scr?.id ?? null;
      }
    }

    return null;
  }, [flowScreens]);

  const ensureValidScreenId = useCallback(
    (preferredId) => {
      if (preferredId && flowScreens.some((screen) => screen.id === preferredId)) {
        return preferredId;
      }
      return firstScreenId;
    },
    [flowScreens, firstScreenId]
  );

  /**
   * Reset flow to first screen when selectedTemplate changes and animate transition
   */
  useEffect(() => {
    const hasScreens = flowScreens.length > 0;

    if (!hasScreens) {
      setActiveFlow(flow_json ?? null);
      setCurrentScreenId(null);
      setNavigationHistory([]);
      setFormData({});
      setScreenData({});
      setIsFlowTransitioning(false);
      setIsFlowOpen(true);
      setHasCompletedFlow(false);
      return;
    }

    const resetFlowState = () => {
      setActiveFlow(flow_json);
      setCurrentScreenId(firstScreenId);
      setNavigationHistory([]);
      setFormData({});
      setScreenData({});
      setIsFlowOpen(true);
      setHasCompletedFlow(false);
      if (firstScreenId && onScreenChange) {
        onScreenChange(firstScreenId);
      }
    };

    if (isInitialRender.current) {
      isInitialRender.current = false;
      resetFlowState();
      setIsFlowTransitioning(false);
      return;
    }

    if (instantUpdate) {
      setActiveFlow(flow_json);
      const nextId = ensureValidScreenId(currentScreenId);
      setCurrentScreenId(nextId);
      setNavigationHistory((prev) =>
        prev.filter((id) => flowScreens.some((screen) => screen.id === id))
      );
      setIsFlowOpen(true);
      setIsFlowTransitioning(false);
      setHasCompletedFlow(false);
      if (nextId && nextId !== currentScreenId && onScreenChange) {
        onScreenChange(nextId);
      }
      return;
    }

    setIsFlowTransitioning(true);
    const transitionTimeout = setTimeout(() => {
      resetFlowState();
      requestAnimationFrame(() => setIsFlowTransitioning(false));
    }, FLOW_TRANSITION_DURATION);

    return () => {
      clearTimeout(transitionTimeout);
    };
  }, [selectedTemplate, flow_json, instantUpdate, flowScreens, firstScreenId, ensureValidScreenId, onScreenChange]);

  useEffect(() => {
    if (!externalCurrentScreenId) return;
    const validTarget = ensureValidScreenId(externalCurrentScreenId);
    if (!validTarget || validTarget === currentScreenId) return;

    setCurrentScreenId(validTarget);
    setNavigationHistory([]);
    setFormData({});
    setScreenData({});
  }, [externalCurrentScreenId, ensureValidScreenId, currentScreenId]);

  const displayedFlow = activeFlow ?? flow_json;

  /**
   * Get the current screen object from the active flow data
   */
  const currentScreen = useMemo(() => {
    return displayedFlow?.screens?.find(
      (screen) => screen.id === currentScreenId
    );
  }, [displayedFlow, currentScreenId]);

  /**
   * Calculate current screen index for progress display
   */
  const allDisplayedScreens = displayedFlow?.screens ?? [];
  const mainDisplayedScreens = useMemo(
    () => allDisplayedScreens.filter((screen) => !isReadMoreScreen(flowScreens, screen)),
    [allDisplayedScreens]
  );

  const effectiveCurrentScreenId = useMemo(() => {
    if (isReadMoreScreen(flowScreens, currentScreen)) {
      return findParentScreenIdForNext(flowScreens, currentScreenId) || currentScreenId;
      // return currentScreen.__meta.parentScreenId || currentScreenId;
    }
    return currentScreenId;
  }, [currentScreen, currentScreenId]);

  const currentScreenIndex = useMemo(() => {
    if (!effectiveCurrentScreenId) return 0;
    const idx = mainDisplayedScreens.findIndex(
      (screen) => screen.id === effectiveCurrentScreenId
    );
    return idx === -1 ? 0 : idx;
  }, [mainDisplayedScreens, effectiveCurrentScreenId]);

  const getScreenIdLabel = (screen, index) =>
    screen?.id || `SCREEN_${index + 1}`;

  const getScreenTitleLabel = (screen, index) => {
    if (!screen) return `Screen ${index + 1}`;
    return screen.title || `Screen ${index + 1}`;
  };

  /**
   * Handle screen selection from dropdown
   * @param {string} screenId - ID of the screen to navigate to
   */
  const handleScreenSelect = (screenId) => {
    if (!screenId) return;

    if (screenId !== currentScreenId) {
      // Don't add to history for dropdown navigation
      setCurrentScreenId(screenId);
      setNavigationHistory([]);
      setFormData({});
      setScreenData({});
    }

    if (onScreenChange) {
      onScreenChange(screenId);
    }
  };

  /**
   * Generate dropdown menu items for screen selection
   */
  const screenDropdownMenu = useMemo(() => {
    if (!displayedFlow?.screens) return null;

    return (
      <Menu
        selectedKeys={[currentScreenId]}
        onClick={({ key }) => handleScreenSelect(key)}
        className="max-h-80 overflow-y-auto"
      >
        {displayedFlow.screens.map((screen, index) => {
          // const isReadMore = Boolean(screen.__meta?.isOptInReadMore);
          const isReadMore = Boolean(isReadMoreScreen(flowScreens, screen));
          const parentId = isReadMore ? findParentScreenIdForNext(flowScreens, screen.id) : screen.id;
          const position = parentId
            ? mainDisplayedScreens.findIndex((main) => main.id === parentId) + 1
            : index + 1;
          const totalCount = Math.max(mainDisplayedScreens.length, 0) || displayedFlow.screens.length;
          return (
            <Menu.Item key={screen.id}>
              <div
                className={`flex items-center justify-between min-w-[220px] ${
                  isReadMore ? 'pl-6' : ''
                }`}
              >
                <div className="flex flex-col">
                  <span className="text-sm font-bold">
                    {getScreenTitleLabel(screen, index)}
                  </span>
                  <span
                    className={`text-xs text-gray-500 ${
                      isReadMore ? 'text-gray-600' : 'text-gray-800'
                    }`}
                  >
                    {isReadMore ? `↳ ${getScreenIdLabel(screen, index)}` : getScreenIdLabel(screen, index)}
                  </span>
                </div>
                <span className="ml-4 text-xs text-gray-500">
                  {position > 0 ? `${position} of ${totalCount}` : ''}
                </span>
              </div>
            </Menu.Item>
          );
        })}
      </Menu>
    );
  }, [displayedFlow, currentScreenId, mainDisplayedScreens]);

  /**
   * Handle navigation to another screen
   * @param {string} nextScreenId - ID of the screen to navigate to
   * @param {Object} payload - Data payload to pass to next screen
   */
  const handleNavigation = (nextScreenId, payload = {}) => {
    if (nextScreenId) {
      setNavigationHistory((prev) =>
        currentScreenId ? [...prev, currentScreenId] : prev
      );
      setCurrentScreenId(nextScreenId);
      setScreenData(payload);
      if (onScreenChange) {
        onScreenChange(nextScreenId);
      }
    }
  };

  const handleGoBack = useCallback(() => {
    setNavigationHistory((prevHistory) => {
      if (prevHistory.length === 0) {
        return prevHistory;
      }

      const historyCopy = [...prevHistory];
      let previousScreenId = historyCopy.pop();

      if (
        previousScreenId &&
        !displayedFlow?.screens?.some((screen) => screen.id === previousScreenId)
      ) {
        previousScreenId = findFirstMainScreenId(displayedFlow?.screens ?? []);
      }

      if (!previousScreenId) {
        previousScreenId = findFirstMainScreenId(displayedFlow?.screens ?? []);
      }

      if (previousScreenId) {
        setCurrentScreenId(previousScreenId);
        setScreenData({});
        if (onScreenChange) {
          onScreenChange(previousScreenId);
        }
      }

      return historyCopy;
    });
  }, [displayedFlow, findFirstMainScreenId, onScreenChange]);

  /**
   * Handle opening the flow preview
  */
  const handleOpenFlow = () => {
    setIsFlowOpen(true);
    // Reset to first screen
    const firstId = findFirstMainScreenId(displayedFlow?.screens ?? []);
    setCurrentScreenId(firstId);
    setNavigationHistory([]);
    setFormData({});
    setScreenData({});
    setHasCompletedFlow(false);
    if (firstId && onScreenChange) {
      onScreenChange(firstId);
    }
  };

  /**
   * Handle close button click - returns to landing screen
   */
  const handleClose = ({ fromCompletion = false } = {}) => {
    setIsFlowOpen(false);
    // Reset state after animation completes
    setTimeout(() => {
      const firstId = findFirstMainScreenId(displayedFlow?.screens ?? []);
      setCurrentScreenId(firstId);
      setNavigationHistory([]);
      setFormData({});
      setScreenData({});
      if (!fromCompletion) {
        setHasCompletedFlow(false);
      }
      if (firstId && onScreenChange) {
        onScreenChange(firstId);
      }
    }, 300);
  };

  /**
   * Handle flow completion
   * @param {Object} payload - Final submission payload
   */
  const handleComplete = (payload) => {
    message.success("Flow completed successfully!");
    // Close the flow after completion
    setHasCompletedFlow(true);
    handleClose({ fromCompletion: true });
  };

  /**
   * Update form data for a specific field
   * @param {string} name - Field name
   * @param {any} value - Field value
   */
  const updateFormData = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * Resolve dynamic data references in text
   * Supports ${form.field} and ${data.field} syntax
   * @param {string} text - Text with potential dynamic references
   * @returns {string} Resolved text
   */
  const resolveDynamicText = (text) => {
    if (typeof text !== "string") return text;

    return text
      .replace(/\$\{form\.(\w+)\}/g, (match, field) => {
        return formData[field] || "";
      })
      .replace(/\$\{data\.(\w+)\}/g, (match, field) => {
        return (
          screenData[field] || currentScreen?.data?.[field]?.__example__ || ""
        );
      });
  };

  /**
   * Validate required fields for a collection of components
   */
  const validateRequiredFields = useCallback(
    (components = []) => {
      const traverse = (list = []) => {
        if (!Array.isArray(list)) return true;

        for (const comp of list) {
          if (!comp || typeof comp !== "object") continue;
          if (comp.visible === false) continue;
          if (comp.type === "Footer") continue;

          if (comp.type === "Form") {
            if (!traverse(comp.children ?? [])) return false;
            continue;
          }

          if (!comp.required) {
            continue;
          }

          const name = comp.name;
          if (!name) {
            continue;
          }

          const value = Object.prototype.hasOwnProperty.call(formData, name)
            ? formData[name]
            : comp["init-value"];

          switch (comp.type) {
            case "TextInput":
            case "TextArea": {
              const str =
                typeof value === "string"
                  ? value
                  : value == null
                  ? ""
                  : String(value);
              if (str.trim().length === 0) {
                return false;
              }
              break;
            }
            case "DatePicker": {
              if (value === undefined || value === null || value === "") {
                return false;
              }
              break;
            }
        case "CheckboxGroup": {
          const arr = Array.isArray(value)
            ? value
            : value !== undefined && value !== null
            ? [].concat(value)
            : [];
          const minSelected =
            comp["min-selected-count"] !== undefined
              ? comp["min-selected-count"]
              : 1;
          if (arr.length < Math.max(minSelected, 1)) {
            return false;
          }
          break;
        }
            case "RadioButtonsGroup":
            case "Dropdown": {
              if (value === undefined || value === null || value === "") {
                return false;
              }
              break;
            }
            case "OptIn": {
              if (value !== true) {
                return false;
              }
              break;
            }
            default: {
              if (value === undefined || value === null || value === "") {
                return false;
              }
            }
          }
        }

        return true;
      };

      return traverse(components);
    },
    [formData]
  );

  /**
   * Render individual components based on their type
   * @param {Object} component - Component object from Flow JSON
   * @param {number} index - Component index for key
   * @returns {JSX.Element} Rendered component
   */
  const renderComponent = (component, index) => {
    // Check visibility
    if (component.visible === false) return null;

    const key = `component-${index}`;

    switch (component.type) {
      case "TextHeading":
        return (
          <div key={key} className="mb-5">
            <h1 className="text-lg font-semibold text-gray-900 leading-snug">
              {resolveDynamicText(component.text)}
            </h1>
          </div>
        );

      case "TextSubheading":
        return (
          <div key={key} className="mb-4">
            <h2 className="text-base font-medium text-gray-800 leading-snug">
              {resolveDynamicText(component.text)}
            </h2>
          </div>
        );

      case "TextBody":
        return (
          <div key={key} className="mb-4">
            <p
              className={`text-sm text-gray-700 leading-relaxed ${
                component["font-weight"] === "bold" ? "font-semibold" : ""
              } ${component["font-weight"] === "italic" ? "italic" : ""} ${
                component.strikethrough ? "line-through" : ""
              }`}
            >
              {resolveDynamicText(component.text)}
            </p>
          </div>
        );

      case "TextCaption":
        return (
          <div key={key} className="mb-3">
            <p
              className={`text-xs text-gray-500 leading-normal ${
                component["font-weight"] === "bold" ? "font-semibold" : ""
              } ${component["font-weight"] === "italic" ? "italic" : ""} ${
                component.strikethrough ? "line-through" : ""
              }`}
            >
              {resolveDynamicText(component.text)}
            </p>
          </div>
        );

      case "TextInput":
        return (
          <div key={key} className="mb-4">
            <input
              type={component["input-type"] || "text"}
              placeholder={resolveDynamicText(component.label)}
              value={formData[component.name] || component["init-value"] || ""}
              onChange={(e) => updateFormData(component.name, e.target.value)}
              maxLength={component["max-chars"] || 80}
              // Removed disabled prop - all inputs are now enabled in preview
              className={INPUT_BASE_CLASSES}
              style={{
                border: "1px solid #ccc",
              }}
            />
            {component["helper-text"] && (
              <p className="text-xs text-gray-500 mt-1.5">
                {resolveDynamicText(component["helper-text"])}
              </p>
            )}
          </div>
        );

      case "TextArea":
        return (
          <div key={key} className="mb-4">
            <textarea
              placeholder={resolveDynamicText(component.label)}
              value={formData[component.name] || component["init-value"] || ""}
              onChange={(e) => updateFormData(component.name, e.target.value)}
              maxLength={component["max-length"] || 600}
              // Removed disabled prop - all inputs are now enabled in preview
              rows={4}
              className={TEXTAREA_BASE_CLASSES}
            />
            {component["helper-text"] && (
              <p className="text-xs text-gray-500 mt-1.5">
                {resolveDynamicText(component["helper-text"])}
              </p>
            )}
          </div>
        );

      case "CheckboxGroup":
        return (
          <div key={key} className="mb-4">
            {component.label && (
              <label className="block text-sm font-normal text-gray-700 mb-3">
                {resolveDynamicText(component.label)}
              </label>
            )}
            {component.description && (
              <p className="text-xs text-gray-500 mb-3">
                {resolveDynamicText(component.description)}
              </p>
            )}
            <div className="space-y-0">
              {component["data-source"]?.map((option, idx) => (
                <label
                  key={option.id}
                  className={`flex items-center justify-between py-3 cursor-pointer hover:bg-gray-50`}
                >
                  <span className="text-sm text-gray-900 flex-1">
                    {option.title}
                    {option.description && (
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {option.description}
                      </span>
                    )}
                  </span>
                  <Checkbox
                    checked={(
                      formData[component.name] ||
                      component["init-value"] ||
                      []
                    ).includes(option.id)}
                    onChange={(e) => {
                      const currentValue =
                        formData[component.name] ||
                        component["init-value"] ||
                        [];
                      const newValue = e.target.checked
                        ? [...currentValue, option.id]
                        : currentValue.filter((id) => id !== option.id);
                      updateFormData(component.name, newValue);
                    }}
                    // Removed disabled prop - all inputs are now enabled in preview
                    className="whatsapp-flow-checkbox !px-0"
                  />
                </label>
              ))}
            </div>
          </div>
        );

      case "RadioButtonsGroup":
        return (
          <div key={key} className="mb-4">
            {component.label && (
              <label className="block text-sm font-normal text-gray-700 mb-3">
                {resolveDynamicText(component.label)}
              </label>
            )}
            {component.description && (
              <p className="text-xs text-gray-500 mb-3">
                {resolveDynamicText(component.description)}
              </p>
            )}
            <div className="space-y-0">
              {component["data-source"]?.map((option, idx) => (
                <label
                  key={option.id}
                  className={`flex items-center justify-between py-3 px-3 cursor-pointer hover:bg-gray-100 rounded-lg`}
                >
                  <span className="text-sm text-gray-900 flex-1">
                    {option.title}
                    {option.description && (
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {option.description}
                      </span>
                    )}
                  </span>
                  <Radio
                    checked={
                      (formData[component.name] || component["init-value"]) ===
                      option.id
                    }
                    onChange={() => updateFormData(component.name, option.id)}
                    // Removed disabled prop - all inputs are now enabled in preview
                    className=""
                  />
                </label>
              ))}
            </div>
          </div>
        );

      case "Dropdown":
        return (
          <div key={key} className="mb-4">
            {component.label && (
              <label className="block text-sm font-normal text-gray-700 mb-2">
                {resolveDynamicText(component.label)}
              </label>
            )}
            <select
              value={formData[component.name] || component["init-value"] || ""}
              onChange={(e) => updateFormData(component.name, e.target.value)}
              // Removed disabled prop - all inputs are now enabled in preview
              className={SELECT_BASE_CLASSES}
            >
              <option value="">Select an option</option>
              {component["data-source"]?.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.title}
                </option>
              ))}
            </select>
          </div>
        );

      case "DatePicker": {
        const minDateLimit = parseDateLimit(component["min-date"]);
        const maxDateLimit = parseDateLimit(component["max-date"]);
        const disabledDate = (current) => {
          if (!current) return false;
          if (minDateLimit && current.isBefore(minDateLimit, "day")) return true;
          if (maxDateLimit && current.isAfter(maxDateLimit, "day")) return true;
          return false;
        };

        return (
          <div key={key} className="mb-4">
            {component.label && (
              <label className="block text-sm font-normal text-gray-700 mb-2">
                {resolveDynamicText(component.label)}
              </label>
            )}
            <DatePicker
              value={
                formData[component.name]
                  ? dayjs(formData[component.name])
                  : null
              }
              onChange={(date) =>
                updateFormData(component.name, date?.valueOf())
              }
              disabledDate={disabledDate}
              className={`w-full h-[44px] text-sm rounded-lg whatsapp-flow-preview-datepicker`}
              format={DATE_FORMAT}
            />
            {component["helper-text"] && (
              <p className="text-xs text-gray-500 mt-1.5">
                {resolveDynamicText(component["helper-text"])}
              </p>
            )}
          </div>
        );
      }

      case "OptIn": {
        const readMoreAction = component["on-click-action"];
        const hasReadMore =
          readMoreAction?.name &&
          (readMoreAction.name === "navigate" ||
            readMoreAction.name === "open_url" ||
            readMoreAction.name === "data_exchange");

        return (
          <div key={key} className="mb-4 space-y-2">
            <label className="flex items-center cursor-pointer">
              <Checkbox
                checked={
                  formData[component.name] || component["init-value"] || false
                }
                onChange={(e) =>
                  updateFormData(component.name, e.target.checked)
                }
                className="whatsapp-flow-checkbox"
              />
              <span className="text-sm text-gray-900 ml-2 leading-normal">
                {resolveDynamicText(component.label)}
                {component.required && (
                  <span className="text-red-500">*</span>
                )}
                {hasReadMore && (
                <span
                  className="ml-1.5 text-xs font-medium text-green-600 hover:text-green-700"
                  onClick={() => handleAction(readMoreAction)}
                >
                  Read more
                </span>
              )}
              </span>
            </label>
          </div>
        );
      }

      case "Image":
        return (
          component.src && (
            <div key={key} className="mb-4">
              <img
                src={`data:image/png;base64,${component.src}`}
                alt={component["alt-text"] || "No Image"}
                className="w-full max-h-80 rounded-lg object-contain"
              style={{
                objectFit: component["scale-type"] || "contain",
              }}
            />
          </div>
        )
        );

      case "EmbeddedLink":
        return (
          <div key={key} className="mb-3">
            <button
              onClick={() => {
                if (component["on-click-action"]) {
                  handleAction(component["on-click-action"]);
                }
              }}
              className="text-sm text-blue-600 hover:underline"
            >
              {resolveDynamicText(component.text)}
            </button>
          </div>
        );

      case "Footer":
        // Footer is rendered separately at the bottom
        return null;

      case "Form":
        // Form is a container for other components
        return (
          <div key={key} className="space-y-1.5">
            {component.children?.map((child, childIndex) =>
              renderComponent(child, childIndex)
            )}
          </div>
        );

      default:
        return null;
    }
  };

  /**
   * Handle action execution (navigate, complete, data_exchange)
   * @param {Object} action - Action object from component
   */
const handleAction = (action) => {
  if (!action) return;
  switch (action.name) {
    case "navigate": {
      const nextScreenName = action.next?.name;
      const payloadData = {};
      // Resolve payload fields (e.g. "${form.name}")
      if (action.payload) {
        Object.entries(action.payload).forEach(([key, value]) => {
          // If value matches "${form.FIELD}", replace with formData[FIELD]
          const match = /^(\$\{form\.([a-zA-Z0-9_]+)\})$/.exec(value);
          if (match && match[2]) {
            payloadData[key] =
              formData[match[2]] !== undefined ? formData[match[2]] : "";
          } else {
            payloadData[key] = value;
          }
        });
      }
      handleNavigation(nextScreenName, payloadData);
      break;
    }
    case "complete": {
      const completeData = {};
      if (action.payload) {
        Object.entries(action.payload).forEach(([key, value]) => {
          // Same as above for completion action
          const match = /^(\$\{form\.([a-zA-Z0-9_]+)\})$/.exec(value);
          if (match && match[2]) {
            completeData[key] =
              formData[match[2]] !== undefined ? formData[match[2]] : "";
          } else {
            completeData[key] = value;
          }
        });
      }
      handleComplete(completeData);
      break;
    }
    case "data_exchange":
      // Static preview mode, do nothing
      message.info("Data exchange not supported in static preview");
      break;
    default:
      console.warn("Unknown action:", action.name);
  }
};

  /**
   * Render the Footer component with navigation button
   * @param {{ footer: Object, scope: Array }} footerInfo - Footer data and components to validate
   * @returns {JSX.Element} Rendered footer
   */
  const normalizeBoolean = (value) => {
    if (typeof value === "boolean") return value;
    if (value == null) return true; // default enabled
    const resolved = resolveDynamicText(value);
    if (typeof resolved === "boolean") return resolved;
    const text = String(resolved).trim().toLowerCase();
    if (!text) return true;
    return ["true", "1", "yes", "y", "enabled"].includes(text);
  };

  const renderFooter = (footerInfo) => {
    const footer = footerInfo?.footer;
    if (!footer) return null;

    const scope = footerInfo.scope ?? currentScreen?.layout?.children ?? [];
    const componentsToValidate = Array.isArray(scope)
      ? scope.filter((component) => component !== footer)
      : [];

    const meetsRequirements = validateRequiredFields(componentsToValidate);

    const baseEnabled = normalizeBoolean(
      footer.enabled !== undefined ? footer.enabled : true
    );
    const isEnabled = baseEnabled && meetsRequirements;
    const label = resolveDynamicText(footer.label);
    const leftCaption = resolveDynamicText(footer["left-caption"]) || "";
    const rightCaption = resolveDynamicText(footer["right-caption"]) || "";
    const centerCaption = resolveDynamicText(footer["center-caption"]) || "";

    const hasCenterCaption = Boolean(centerCaption);
    const hasSideCaptions = Boolean(leftCaption || rightCaption);

    return (
      <div className="bg-white">
        <div className="px-4 pt-4 pb-3">
          <button
            onClick={() => handleAction(footer["on-click-action"])}
            className={`w-full py-2 px-4 font-medium transition-colors text-xs font-medium text-center rounded-full ${
              isEnabled
                ? "cursor-pointer bg-[#1daa61] text-white"
                : "bg-[#F5F5F5] text-[#BDBDBD] rounded-full cursor-not-allowed"
            }`}
            disabled={!isEnabled}
          >
            {label || "Continue"}
          </button>
        </div>
        {hasCenterCaption ? (
          <div className="px-4 pb-4 text-center">
            <p className="text-[11px] text-gray-500">{centerCaption}</p>
          </div>
        ) : hasSideCaptions ? (
          <div className="px-4 pb-4">
            <div className="flex items-center justify-between text-[11px] text-gray-500">
              <span className="truncate pr-2">{leftCaption}</span>
              <span className="truncate pl-2 text-right">{rightCaption}</span>
            </div>
          </div>
        ) : (
          <div className="px-4 pb-4 text-center">
            <p className="text-[11px] text-gray-500">
              Managed by the business.{" "}
              <a href="#" className="text-green-600 hover:underline">
                Learn more
              </a>
            </p>
          </div>
        )}
      </div>
    );
  };

  /**
   * Find and return the Footer component from screen layout
   */
  const getFooterComponent = () => {
    const searchFooter = (components = [], scope = components) => {
      if (!Array.isArray(components)) return null;
      for (let i = components.length - 1; i >= 0; i -= 1) {
        const component = components[i];
        if (!component || typeof component !== "object" || component.visible === false) {
          continue;
        }
        if (component.type === "Footer") {
          return { footer: component, scope };
        }
        if (component.type === "Form") {
          const formChildren = component.children ?? [];
          const nestedFooter = searchFooter(formChildren, formChildren);
          if (nestedFooter) return nestedFooter;
          continue;
        }
        if (Array.isArray(component.children) && component.children.length > 0) {
          const nestedFooter = searchFooter(component.children, scope);
          if (nestedFooter) return nestedFooter;
        }
      }
      return null;
    };

    return searchFooter(currentScreen?.layout?.children, currentScreen?.layout?.children);
  };

  /**
   * Render progress stepper showing screen completion status
   * Matches Meta Flow Builder design with green bars
   */
  const renderProgressStepper = () => {
    const totalScreens = mainDisplayedScreens.length;
    if (totalScreens === 0) return null;
    const currentIndex = Math.min(currentScreenIndex, totalScreens - 1);

    return (
      <div className="flex items-center gap-1.5 mb-3">
        {Array.from({ length: totalScreens }).map((_, index) => (
          <div
            key={index}
            className={`h-1 flex-1 rounded-full transition-all duration-300 ${
              index < currentIndex
                ? "bg-[#25D366]" // Completed screens - WhatsApp green
                : index === currentIndex
                ? "bg-[#25D366]" // Current screen - WhatsApp green
                : "bg-gray-300" // Upcoming screens - gray
            }`}
          />
        ))}
      </div>
    );
  };

  /**
   * Render the landing screen with "Preview Flow" button
   * Matches Meta's exact design from the screenshot
   */
  const renderLandingScreen = () => {
    return (
      <div className="relative w-full max-w-[380px] mx-auto">
        {/* Outer container with rounded corners and gray border */}
        <div className="bg-white rounded-[20px] shadow-lg overflow-hidden border-8 border-gray-200">
          {/* Top header section - white background */}
          <div className="bg-white px-4 py-4 flex items-center gap-3">
            {/* WhatsApp icon */}
            <svg
              role="img"
              width="36px"
              height="36px"
              viewBox="0 0 40 40"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="20" cy="20" r="20" fill="#1DAA61"></circle>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M26.0704 15.963C26.5687 15.963 27.0139 16.2747 27.1842 16.7431L28.6926 20.8913C28.9163 21.5064 28.599 22.1864 27.9838 22.4101C27.854 22.4573 27.7169 22.4815 27.5788 22.4815H27.4931V26.9259C27.4931 27.4168 27.0951 27.8148 26.6042 27.8148C26.1133 27.8148 25.7153 27.4168 25.7153 26.9259V22.4815H22.1598V26.6296C22.1598 27.2842 21.6291 27.8148 20.9746 27.8148H14.4561C13.8015 27.8148 13.2709 27.2842 13.2709 26.6296V22.4815H13.1852C12.5306 22.4815 12 21.9509 12 21.2963C12 21.1582 12.0241 21.0211 12.0713 20.8913L13.5798 16.7431C13.7501 16.2747 14.1952 15.963 14.6936 15.963H26.0704ZM20.382 22.4815H15.0487V26.037H20.382V22.4815ZM26.0116 13C26.5025 13 26.9005 13.398 26.9005 13.8889C26.9005 14.3798 26.5025 14.7778 26.0116 14.7778H14.7524C14.2614 14.7778 13.8635 14.3798 13.8635 13.8889C13.8635 13.398 14.2614 13 14.7524 13H26.0116Z"
                fill="white"
                stroke="#FEFEFE"
              ></path>
            </svg>

            {/* Progress bar - green */}
            <div className="flex-1">
              <div className="h-1.5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#25D366] rounded-full"
                  style={{ width: "60%" }}
                ></div>
              </div>
            </div>
          </div>

          {/* Main content area - beige/tan background */}
          <div className="bg-[#E5DDD5] px-4 py-6 min-h-[500px]">
            {/* Top right progress indicator - light green */}
            <div
              className="flex justify-end mb-6 ml-[140px] bg-[#d9fdd3] h-8 items-center justify-center"
              style={{ borderRadius: "10px 0px 10px 10px" }}
            >
              <div className="h-2 bg-[#9bbb8bcc] w-[80%] rounded-full"></div>
            </div>

            {/* Preview card - white background */}
            <div
              className="bg-white shadow-sm p-4 mb-6"
              style={{ borderRadius: "0px 10px 10px" }}
            >
              {hasCompletedFlow ? (
                <div className="flex flex-col items-center text-center space-y-4 py-2">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E8F5E9] text-[#2E7D32]">
                    <CheckOutlined />
                  </span>
                  <div className="space-y-1 px-4">
                    <h4 className="text-sm font-semibold text-gray-900">
                      Preview finished
                    </h4>
                    <p className="text-xs text-gray-500">
                      Restart to walk through the flow again or review a different screen.
                    </p>
                  </div>
                  <button
                    onClick={handleOpenFlow}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-blue-200 bg-green-50 px-4 py-2 text-sm font-semibold text-green-700 transition hover:bg-green-100"
                  >
                    Restart preview
                  </button>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {/* Placeholder lines */}
                    <div
                      className="h-2.5 bg-gray-200 rounded"
                      style={{ width: "70%" }}
                    ></div>
                    <div
                      className="h-2.5 bg-gray-200 rounded"
                      style={{ width: "50%" }}
                    ></div>
                  </div>

                  {/* Preview Flow button */}
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <button
                      onClick={handleOpenFlow}
                      className="text-blue-600 text-[14px] w-full font-medium hover:text-blue-700 transition-colors flex justify-center gap-2"
                    >
                      Preview Flow
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Flow Preview Modal with slide animation */}
          {renderFlowPreview()}
        </div>
      </div>
    );
  };

  /**
   * Render the flow preview with slide animation
   * Opens within the component container, not fullscreen
   */
  const renderFlowPreview = () => {
    if (!currentScreen) return null;

    return (
      <div
        className={`absolute inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center transition-opacity duration-300 rounded-[20px] overflow-hidden ${
          isFlowOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => handleClose()}
      >
        <div
          className={`w-full transition-transform duration-300 ease-out ${
            isFlowOpen ? "translate-y-0" : "translate-y-full"
          }`}
          style={{ height: "90%" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Outer gray container */}
          <div className="p-3 h-full">
            {/* Inner white container - the actual flow preview */}
            <div
              className={`bg-white relative rounded-[14px] overflow-hidden shadow-md h-full flex flex-col transition-opacity ${
                isFlowTransitioning
                  ? "opacity-0 pointer-events-none"
                  : "opacity-100"
              }`}
              style={{ transitionDuration: `${FLOW_TRANSITION_DURATION}ms` }}
            >
              {/* Header */}
              <div className="bg-white px-4 py-3 flex items-center gap-3 flex-shrink-0">
                {navigationHistory.length > 0 ? (
                  <button
                    onClick={handleGoBack}
                    className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
                    aria-label="Go back"
                  >
                    <LeftOutlined className="text-gray-700 text-[16px]" />
                  </button>
                ) : (
                  <div className="w-8 h-8" />
                )}
                <div className="flex-1 text-sm text-gray-900 font-normal text-center truncate">
                  {currentScreen.title || "Untitled Screen"}
                </div>
                <button
                  onClick={() => handleClose()}
                  className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Close preview"
                >
                  <CloseOutlined className="text-gray-700 text-[16px]" />
                </button>
              </div>

              {/* Progress Stepper - Below header */}
              <div className="px-4 pt-3 bg-white flex-shrink-0">
                {renderProgressStepper()}
              </div>

              {/* Screen content */}
              <div className="px-5 py-5 overflow-y-auto bg-white flex-1">
                {currentScreen.layout?.type === "SingleColumnLayout" && (
                  <div className="gap-y-1">
                    {currentScreen.layout.children
                      ?.filter((component) => component.type !== "Footer")
                      .map((component, index) =>
                        renderComponent(component, index)
                      )}
                  </div>
                )}
              </div>

              {/* Footer - Always rendered at the bottom */}
              {renderFooter(getFooterComponent())}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main render - Error states
  if (
    !displayedFlow ||
    !displayedFlow.screens ||
    displayedFlow.screens.length === 0
  ) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
        <p className="text-gray-500 text-sm">No flow data available</p>
      </div>
    );
  }

  return (
    <>
      <aside className="whatsapp-flow-preview rounded-xl p-[1px] bg-gradient-to-b from-[#e8ecf7] to-[#dfe9f3] lg:sticky lg:top-6">
        <div className="rounded-xl bg-white p-5">
          {/* Header with Preview title and Screen Dropdown */}
          <div className="mb-3 flex items-center justify-between px-4">
            <h3 className="text-sm font-semibold text-gray-800">Preview</h3>

            {/* Screen Selector Dropdown */}
            <Dropdown
              overlay={screenDropdownMenu}
              trigger={["click"]}
              placement="bottomRight"
            >
              <button className="flex items-center gap-3 px-3 py-1.5 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors min-w-[180px] justify-between text-left">
                <div className="flex flex-col">
                  {/* <span className="text-[11px] uppercase tracking-wide text-gray-400">Screen ID</span>
                  <span className="text-sm font-semibold text-gray-700 truncate">
                    {currentScreen?.id || "Select a screen"}
                  </span> */}
                  {currentScreen?.title && (
                    <span className="text-sm font-semibold text-gray-700 truncate">
                      {currentScreen.title || "Select a screen"}
                    </span>
                  )}
                </div>
                <DownOutlined className="text-gray-500 text-xs" />
              </button>
            </Dropdown>
          </div>

          {/* Landing Screen */}
          {renderLandingScreen()}
        </div>
      </aside>
      <style jsx global>{`
        .whatsapp-flow-preview input:focus,
        .whatsapp-flow-preview input:focus-visible,
        .whatsapp-flow-preview textarea:focus,
        .whatsapp-flow-preview textarea:focus-visible,
        .whatsapp-flow-preview select:focus,
        .whatsapp-flow-preview select:focus-visible {
          outline: none;
          border-color: #1daa61 !important;
          box-shadow: 0 0 0 3px rgba(29, 170, 97, 0.15);
        }

        .whatsapp-flow-preview select,
        .whatsapp-flow-preview input,
        .whatsapp-flow-preview textarea {
          border-color: #d1d5db;
        }

        .whatsapp-flow-preview .ant-picker {
          border-radius: 0.75rem;
          border-color: #d1d5db;
        }

        .whatsapp-flow-preview .ant-picker,
        .whatsapp-flow-preview .ant-picker input {
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .whatsapp-flow-preview .ant-picker-focused,
        .whatsapp-flow-preview .ant-picker:hover {
          border-color: #1daa61 !important;
          box-shadow: 0 0 0 3px rgba(29, 170, 97, 0.15);
        }

        .whatsapp-flow-preview .ant-picker-focused {
          outline: none;
        }

        .whatsapp-flow-preview .ant-checkbox-inner,
        .whatsapp-flow-preview .ant-radio-inner {
          transition: border-color 0.2s ease, background-color 0.2s ease,
            box-shadow 0.2s ease;
        }

        .whatsapp-flow-preview .ant-checkbox .ant-checkbox-inner {
          border-radius: 4px;
        }

        .whatsapp-flow-preview .ant-checkbox-checked .ant-checkbox-inner {
          background-color: #1daa61;
          border-color: #1daa61;
        }

        .whatsapp-flow-preview .ant-checkbox-input:focus + .ant-checkbox-inner,
        {
          border-color: #1daa61;
          box-shadow: 0 0 0 3px rgba(29, 170, 97, 0.15);
        }

        .whatsapp-flow-preview .ant-checkbox-wrapper:hover .ant-checkbox-inner,
        .whatsapp-flow-preview .ant-checkbox:hover .ant-checkbox-inner {
          border-color: #1daa61;
        }

      `}</style>
    </>
  );
};

export default WhatsAppFlowPreview;
